import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AbstractSignOffProcessService } from '@data-terminal/feature-dialogs/services/processes/abstract-sign-off-process.service';
import { SignOffService } from '@data-terminal/feature-dialogs/services/sign-off-service/sign-off.service';
import { UserMachinesService } from '@data-terminal/data-access/user-machines';
import { NotificationSendingService } from '@data-terminal/data-access/notifications/notification-sending.service';
import { HplusRedirectionService, SnackBarService } from '@data-terminal/utils/services';
import { DataTerminalSessionService } from '@data-terminal/data-access/session-service';
import { PureTimeApiService } from '@data-terminal/feature-workstation-details/services/pure-time-api/pure-time-api.service';
import { TransitionService } from '@data-terminal/feature-transition/services';
import { TimeModeEntry } from '@data-terminal/shared-models/time-mode-entry.interface';
import { shouldDisplayDialog } from '@data-terminal/feature-dialogs/services/processes/sign-off-process.functions';
import { SignOffDialogComponent } from '@data-terminal/feature-dialogs/dialogs/sign-off-dialog/sign-off-dialog.component';
import { SignOffMachine } from '@data-terminal/shared-models/machine';
import { User } from '@data-terminal/shared-models/session';
import { FarewellService } from '@data-terminal/base/app/services/farewell.service';
import { GeneralActivitiesService } from '@data-terminal/feature-workstation-details/services/general-activities/general-activities.service';

@Injectable({
    providedIn: 'root',
})
export class SignOffProcessService extends AbstractSignOffProcessService {
    protected readonly dialog: MatDialog;
    protected readonly signOffService: SignOffService;
    protected readonly router: Router;
    protected readonly activatedRoute: ActivatedRoute;
    protected readonly userMachinesService: UserMachinesService;
    protected readonly notificationService: NotificationSendingService;
    protected readonly translate: TranslateService;
    protected readonly snackBarService: SnackBarService;
    protected readonly sessionService: DataTerminalSessionService;
    protected readonly hplusRedirectionService: HplusRedirectionService;
    protected readonly pureTimeApiService: PureTimeApiService;
    protected readonly farewellService: FarewellService;
    protected readonly transitionService: TransitionService;
    protected readonly generalActivitiesService: GeneralActivitiesService;

    constructor() {
        const dialog = inject(MatDialog);
        const signOffService = inject(SignOffService);
        const router = inject(Router);
        const activatedRoute = inject(ActivatedRoute);
        const userMachinesService = inject(UserMachinesService);
        const notificationService = inject(NotificationSendingService);
        const translate = inject(TranslateService);
        const snackBarService = inject(SnackBarService);
        const sessionService = inject(DataTerminalSessionService);
        const hplusRedirectionService = inject(HplusRedirectionService);
        const pureTimeApiService = inject(PureTimeApiService);
        const farewellService = inject(FarewellService);
        const transitionService = inject(TransitionService);
        const generalActivitiesService = inject(GeneralActivitiesService);

        super(
            dialog,
            signOffService,
            router,
            activatedRoute,
            userMachinesService,
            notificationService,
            translate,
            snackBarService,
            sessionService,
            hplusRedirectionService,
            pureTimeApiService,
            farewellService,
            transitionService,
            generalActivitiesService
        );

        this.dialog = dialog;
        this.signOffService = signOffService;
        this.router = router;
        this.activatedRoute = activatedRoute;
        this.userMachinesService = userMachinesService;
        this.notificationService = notificationService;
        this.translate = translate;
        this.snackBarService = snackBarService;
        this.sessionService = sessionService;
        this.hplusRedirectionService = hplusRedirectionService;
        this.pureTimeApiService = pureTimeApiService;
        this.farewellService = farewellService;
        this.transitionService = transitionService;
        this.generalActivitiesService = generalActivitiesService;
    }

    protected prepareStream(unsubmittedTimeEntries: TimeModeEntry[], user: User): Observable<SignOffMachine[]> {
        return shouldDisplayDialog(user.userId, this.userMachinesService.getAssignedMachines(), unsubmittedTimeEntries)
            ? this.openDialog(SignOffDialogComponent, unsubmittedTimeEntries)
            : of(this.signOffMachines);
    }
}
